import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Button, Space, Spin, Alert, Typography, Tooltip, Tag, Radio } from 'antd';
import {
  DownloadOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
  ReloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  FilePdfOutlined,
} from '@ant-design/icons';
import { Document, Page, pdfjs } from 'react-pdf';
import styled from 'styled-components';
import api from '../../services/api';
import { useGlobalContext } from '../../store/GlobalContext';

// Ensure pdfjs worker is configured
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const { Text, Title } = Typography;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #f0f0f0;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  padding: 8px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  margin: 12px 0;
`;

const PdfCanvasContainer = styled.div<{ $isFullscreen?: boolean }>`
  min-height: ${props => (props.$isFullscreen ? 'calc(100vh - 200px)' : '62vh')};
  max-height: ${props => (props.$isFullscreen ? 'calc(100vh - 180px)' : '70vh')};
  overflow: auto;
  background: #525659;
  border-radius: 6px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.2);

  .react-pdf__Document {
    display: flex;
    justify-content: center;
  }

  .react-pdf__Page {
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
    border-radius: 2px;
    background: #ffffff;
    margin-bottom: 16px;
  }
`;

const CenterSpin = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: 12px;
  color: #fff;
`;

interface PdfPreviewModalProps {
  visible: boolean;
  onClose: () => void;
  probateDocId: string;
  docTitle?: string;
  estateId?: string;
  estateName?: string;
}

export const PdfPreviewModal: React.FC<PdfPreviewModalProps> = ({
  visible,
  onClose,
  probateDocId,
  docTitle,
  estateId: propEstateId,
  estateName: propEstateName,
}) => {
  const { selectedEstate } = useGlobalContext();
  const effectiveEstateId = propEstateId || selectedEstate?.uuid;
  const effectiveEstateName = propEstateName || selectedEstate?.estateName;

  const [mode, setMode] = useState<'filled' | 'original'>('filled');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.1);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [downloading, setDownloading] = useState<boolean>(false);

  // Fetch the PDF binary (filled or original)
  const fetchPdf = useCallback(async () => {
    if (!probateDocId) return;

    setLoading(true);
    setError(null);

    // Clean up previous blob URL
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setPdfBlob(null);

    const isFilled = mode === 'filled' && !!effectiveEstateId;
    const path = isFilled
      ? `/probatedocs/${probateDocId}/download/filled/${effectiveEstateId}`
      : `/probatedocs/${probateDocId}/download/original`;

    try {
      const response = await api.get(path, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const objectUrl = window.URL.createObjectURL(blob);
      setPdfBlob(blob);
      setPdfUrl(objectUrl);
      setCurrentPage(1);
    } catch (err: any) {
      console.error('Failed to load PDF preview:', err);
      let errorMsg = 'Failed to load PDF preview.';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const parsed = JSON.parse(text);
          if (parsed?.message) errorMsg = parsed.message;
        } catch {
          // ignore
        }
      } else if (err.response?.data?.message) {
        errorMsg = err.response.data.message;
      }
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [probateDocId, effectiveEstateId, mode]);

  useEffect(() => {
    if (visible && probateDocId) {
      if (!effectiveEstateId && mode === 'filled') {
        setMode('original');
      } else {
        fetchPdf();
      }
    } else {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
      setPdfBlob(null);
      setNumPages(0);
      setCurrentPage(1);
      setError(null);
    }
  }, [visible, probateDocId, mode, effectiveEstateId]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const handleDownload = () => {
    if (!pdfBlob) return;
    setDownloading(true);
    try {
      const cleanTitle = (docTitle || 'document').replace(/[^a-zA-Z0-9_-]/g, '_');
      const suffix = mode === 'filled' && effectiveEstateName
        ? `filled_${effectiveEstateName.replace(/[^a-zA-Z0-9_-]/g, '_')}`
        : mode;
      const filename = `${cleanTitle}_${suffix}.pdf`;

      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDocumentLoadSuccess = ({ numPages: count }: { numPages: number }) => {
    setNumPages(count);
    setCurrentPage(1);
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width={isFullscreen ? '96vw' : 920}
      style={isFullscreen ? { top: 12 } : { top: 32 }}
      destroyOnClose
      styles={{
        body: { padding: '16px 24px 20px' }
      }}
      title={null}
    >
      <ModalHeader>
        <div>
          <Title level={4} style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
            <FilePdfOutlined style={{ color: '#EF4444' }} />
            {docTitle || 'Document Preview'}
          </Title>
          <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
            {effectiveEstateName ? (
              <Tag color="blue">Estate: {effectiveEstateName}</Tag>
            ) : (
              <Tag color="orange">No Estate Selected</Tag>
            )}
            <Radio.Group
              size="small"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={!effectiveEstateId && mode === 'original'}
            >
              <Radio.Button value="filled" disabled={!effectiveEstateId}>
                Prefilled PDF
              </Radio.Button>
              <Radio.Button value="original">Blank Template</Radio.Button>
            </Radio.Group>
          </div>
        </div>

        <Space size="small">
          <Tooltip title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <Button
              icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={() => setIsFullscreen(!isFullscreen)}
            />
          </Tooltip>
          <Button
            type="primary"
            icon={<DownloadOutlined />}
            onClick={handleDownload}
            disabled={!pdfBlob || loading}
            loading={downloading}
            style={{ backgroundColor: '#10B981', borderColor: '#10B981' }}
          >
            Download PDF
          </Button>
        </Space>
      </ModalHeader>

      <Toolbar>
        <Space size="small">
          <Button
            size="small"
            icon={<LeftOutlined />}
            disabled={currentPage <= 1 || loading}
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
          />
          <Text style={{ minWidth: 80, textAlign: 'center' }}>
            Page {currentPage} of {numPages || 1}
          </Text>
          <Button
            size="small"
            icon={<RightOutlined />}
            disabled={currentPage >= numPages || loading}
            onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
          />
        </Space>

        <Space size="small">
          <Tooltip title="Zoom Out">
            <Button
              size="small"
              icon={<ZoomOutOutlined />}
              disabled={scale <= 0.5 || loading}
              onClick={() => setScale(s => Math.max(0.5, +(s - 0.15).toFixed(2)))}
            />
          </Tooltip>
          <Text style={{ minWidth: 44, textAlign: 'center' }}>
            {Math.round(scale * 100)}%
          </Text>
          <Tooltip title="Zoom In">
            <Button
              size="small"
              icon={<ZoomInOutlined />}
              disabled={scale >= 2.5 || loading}
              onClick={() => setScale(s => Math.min(2.5, +(s + 0.15).toFixed(2)))}
            />
          </Tooltip>
          <Button size="small" onClick={() => setScale(1.0)}>
            Fit
          </Button>
          <Tooltip title="Reload document">
            <Button
              size="small"
              icon={<ReloadOutlined />}
              onClick={fetchPdf}
              loading={loading}
            />
          </Tooltip>
        </Space>
      </Toolbar>

      {error ? (
        <Alert
          message="Failed to load PDF preview"
          description={error}
          type="error"
          showIcon
          style={{ margin: '20px 0' }}
          action={
            <Button size="small" type="primary" onClick={fetchPdf}>
              Retry
            </Button>
          }
        />
      ) : (
        <PdfCanvasContainer $isFullscreen={isFullscreen}>
          {loading ? (
            <CenterSpin>
              <Spin size="large" />
              <Text style={{ color: '#fff' }}>Generating preview...</Text>
            </CenterSpin>
          ) : pdfUrl ? (
            <Document
              file={pdfUrl}
              onLoadSuccess={handleDocumentLoadSuccess}
              loading={
                <CenterSpin>
                  <Spin size="large" />
                  <Text style={{ color: '#fff' }}>Loading PDF...</Text>
                </CenterSpin>
              }
              error={
                <Alert
                  message="Failed to parse PDF"
                  description="The document could not be rendered."
                  type="error"
                  showIcon
                />
              }
            >
              <Page
                key={`page_${currentPage}_scale_${scale}`}
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Document>
          ) : null}
        </PdfCanvasContainer>
      )}
    </Modal>
  );
};

export default PdfPreviewModal;
